import { ErrorModal } from "@dexteel/mesf-core";
import {
	Box,
	CircularProgress,
	Divider,
	Paper,
	Typography,
} from "@mui/material";
import type { ExcelRow, GridApi } from "ag-grid-community";
import moment from "moment";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { mesfColors } from "@/setup/ag-grid/mesf-theme";
import {
	type ChartDisplayMode,
	type InformationRetrievalFilters as Filters,
	SUMMARY_PROPERTIES,
} from "../models/InformationRetrieval";
import {
	useInformationRetrievalData,
	useInformationRetrievalPickers,
} from "../hooks/useInformationRetrieval";
import {
	buildGroups,
	buildRecipeLegend,
	buildSummary,
	MILL_STANDS,
} from "../utils/aggregate";
import {
	ALL_SHIFTS,
	filterToDrop,
	getFilterOptions,
} from "../utils/filterOptions";
import { BuildRecipeModal } from "./BuildRecipeModal";
import { GroupsGrid } from "./GroupsGrid";
import { InformationRetrievalChart } from "./InformationRetrievalChart";
import { InformationRetrievalFilters } from "./InformationRetrievalFilters";
import { SummaryGrid } from "./SummaryGrid";

const DATE_FORMAT = "YYYY-MM-DD";

const URL_SCHEMA = {
	// The legacy screen opens on today, not on a rolling window.
	start: urlParam.date(() => moment().startOf("day")),
	end: urlParam.date(() => moment().startOf("day")),
	productTypeId: urlParam.int(),
	productSizeId: urlParam.int(),
	shiftId: urlParam.string(ALL_SHIFTS),
	groupByDate: urlParam.bool(true),
	groupByShift: urlParam.bool(true),
};

const textCell = (value: string): ExcelRow["cells"][number] => ({
	data: { value, type: "String" },
});

export const InformationRetrieval = () => {
	// The legacy screen opens on today, not on a rolling window.
	// The selected group rows are not kept: the grid selects them all on load and their
	// keys change completely with the range, so a link would carry a list that no longer
	// matches what came back.
	const [
		{
			start,
			end,
			productTypeId,
			productSizeId,
			shiftId,
			groupByDate,
			groupByShift,
		},
		setFilters,
		{ dropValues, isDefault },
	] = useUrlState(URL_SCHEMA);

	const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
	const [displayMode, setDisplayMode] = useState<ChartDisplayMode>("average");
	const [rangeEnabled, setRangeEnabled] = useState(true);
	const [recipeRangeEnabled, setRecipeRangeEnabled] = useState(true);
	const [isRecipeModalOpen, setIsRecipeModalOpen] = useState(false);
	const [errorMsg, setErrorMsg] = useState("");
	const summaryApiRef = useRef<GridApi | null>(null);

	const startStr = start.format(DATE_FORMAT);
	const endStr = end.format(DATE_FORMAT);

	const filters: Filters = useMemo(
		() => ({
			start: startStr,
			end: endStr,
			productTypeId,
			productSizeId,
			shiftId,
			groupByDate,
			groupByShift,
		}),
		[
			startStr,
			endStr,
			productTypeId,
			productSizeId,
			shiftId,
			groupByDate,
			groupByShift,
		],
	);

	const pickersQuery = useInformationRetrievalPickers(startStr, endStr);

	const selection = useMemo(
		() => ({ shiftId, productTypeId, productSizeId }),
		[shiftId, productTypeId, productSizeId],
	);
	const filterOptions = useMemo(
		() => getFilterOptions(pickersQuery.data, selection),
		[pickersQuery.data, selection],
	);
	const toDrop = pickersQuery.data
		? filterToDrop(pickersQuery.data, selection)
		: null;
	const selectionReady = pickersQuery.data != null && toDrop === null;

	// New dates can leave a selection that did not run together. Reset one
	// filter per pass; the next render re-checks what is left.
	useEffect(() => {
		if (toDrop === "product") dropValues("productSizeId");
		else if (toDrop === "productType") dropValues("productTypeId");
		else if (toDrop === "shift") dropValues("shiftId");
	}, [toDrop, dropValues]);

	const dataQuery = useInformationRetrievalData(filters, selectionReady);

	const rows = useMemo(() => dataQuery.data ?? [], [dataQuery.data]);
	const groups = useMemo(() => buildGroups(rows), [rows]);
	const stands = useMemo(() => [...MILL_STANDS], []);

	// The grid owns the selection and reports it up; it selects everything on
	// first render, as the legacy screen does.
	const summary = useMemo(
		() => buildSummary(groups, selectedKeys, stands),
		[groups, selectedKeys, stands],
	);
	const recipeLegend = useMemo(
		() => buildRecipeLegend(groups, selectedKeys),
		[groups, selectedKeys],
	);

	// Neither grouping can be switched off on its own: with both off the
	// procedure collapses everything into a single unlabelled row. The handlers below keep
	// that true for a click; this keeps it true for a link, which arrives without passing
	// through them.
	useEffect(() => {
		if (!groupByDate && !groupByShift) setFilters({ groupByDate: true });
	}, [groupByDate, groupByShift, setFilters]);

	const handleGroupByDate = useCallback(
		(value: boolean) => {
			// One write: two calls in the same handler would both read the same URL.
			setFilters({
				groupByDate: value,
				...(!value && !groupByShift ? { groupByShift: true } : {}),
			});
		},
		[groupByShift, setFilters],
	);

	const handleGroupByShift = useCallback(
		(value: boolean) => {
			setFilters({
				groupByShift: value,
				...(!value && !groupByDate ? { groupByDate: true } : {}),
			});
		},
		[groupByDate, setFilters],
	);

	// Back to the opening range. The product stays: it is dropped by the usual
	// rule only if it did not run today.
	const handleReset = useCallback(
		() =>
			setFilters({
				start: moment().startOf("day"),
				end: moment().startOf("day"),
				shiftId: ALL_SHIFTS,
				productTypeId: null,
			}),
		[setFilters],
	);

	/**
	 * The file carries both grids: the per-group block first, four rows per
	 * date/shift the way the legacy export lays it out, then the aggregate.
	 */
	const handleExportToExcel = useCallback(() => {
		if (!summaryApiRef.current) return;

		// Column layout copied from the legacy export: Date, the series name, then
		// one column per stand. The series column is what makes the four rows of a
		// group readable in a spreadsheet — without it nobody can tell which line
		// is the actual and which the tolerance.
		const header: ExcelRow = {
			cells: [
				textCell("Date"),
				textCell("R-Factor"),
				...stands.map((s) => textCell(s)),
			],
		};

		const SERIES = [
			{ field: "avg", label: "Current" },
			{ field: "target", label: "Target" },
			{ field: "min", label: "Min" },
			{ field: "max", label: "Max" },
		] as const;

		const groupRows: ExcelRow[] = groups.flatMap((group) =>
			SERIES.map(({ field, label }) => ({
				cells: [
					textCell(group.label),
					textCell(label),
					...stands.map((stand) => {
						const cell = group.stands[stand];
						const value = cell?.active ? cell[field] : null;
						return textCell(value == null ? "" : value.toFixed(3));
					}),
				],
			})),
		);

		// The aggregate block is written by hand too, with the same two leading
		// columns, so both tables line up under one set of stand headings. The
		// grid's own rows are skipped — otherwise they would come out shifted a
		// column to the left.
		const summaryRows: ExcelRow[] = SUMMARY_PROPERTIES.map((prop) => ({
			cells: [
				textCell(""),
				textCell(prop.label),
				...stands.map((stand) => {
					const value = summary[prop.key][stand];
					if (value === null || value === undefined) return textCell("");
					if (prop.format === "PERCENT")
						return textCell(`${value.toFixed(0)}%`);
					return textCell(value.toFixed(prop.format === "FLOAT2" ? 2 : 3));
				}),
			],
		}));

		summaryApiRef.current.exportDataAsExcel({
			fileName: "Mill_Information_Retrieval.xlsx",
			// Both blocks are written by hand, so the grid contributes nothing:
			// its rows are skipped and its header suppressed. Without the second
			// flag the summary grid's own header lands at the foot of the sheet,
			// one column out of step with everything above it.
			shouldRowBeSkipped: () => true,
			skipColumnHeaders: true,
			prependContent: [
				header,
				...groupRows,
				{ cells: [] },
				{ cells: [textCell(""), textCell(`Recipe: ${recipeLegend}`)] },
				...summaryRows,
			],
		});
	}, [groups, stands, summary, recipeLegend]);

	const error = dataQuery.error ?? pickersQuery.error;
	if (error && !errorMsg) {
		setErrorMsg((error as Error).message);
	}

	const hasData = groups.length > 0;

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 1.5,
				p: 1.5,
				bgcolor: "#f0f2f5",
				minHeight: "calc(100vh - 130px)",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Mill Information Retrieval
			</Typography>

			<Paper elevation={3}>
				<InformationRetrievalFilters
					start={start}
					end={end}
					onStartChange={(d) =>
						isUsableDate(d) && d && setFilters({ start: d })
					}
					onEndChange={(d) => isUsableDate(d) && d && setFilters({ end: d })}
					productTypeId={productTypeId}
					productSizeId={productSizeId}
					shiftId={shiftId}
					groupByDate={groupByDate}
					groupByShift={groupByShift}
					onProductTypeChange={(id) => setFilters({ productTypeId: id })}
					onProductSizeChange={(id) => setFilters({ productSizeId: id })}
					onShiftChange={(id) => setFilters({ shiftId: id })}
					onGroupByDateChange={handleGroupByDate}
					onGroupByShiftChange={handleGroupByShift}
					productTypes={filterOptions.productTypes}
					productSizes={filterOptions.productSizes}
					crews={filterOptions.crews}
					canUseCurrentShift={filterOptions.canUseCurrentShift}
					hasProduction={filterOptions.hasProduction}
					isLoadingOptions={pickersQuery.isFetching}
					onReset={handleReset}
					onExportExcel={handleExportToExcel}
					onBuildRecipe={() => setIsRecipeModalOpen(true)}
					canBuildRecipe={hasData}
					canReset={!isDefault("start", "end", "shiftId", "productTypeId")}
				/>
			</Paper>

			<Paper elevation={3} sx={{ p: 1.5, flex: 1 }}>
				{dataQuery.isInitialLoading || pickersQuery.isInitialLoading ? (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							minHeight: 320,
						}}
					>
						<CircularProgress />
					</Box>
				) : !hasData ? (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							minHeight: 320,
							color: mesfColors.text.secondary,
						}}
					>
						{/* An empty picker means nothing ran in those dates, which is a
						    fact about production — saying "select a product" when there
						    is none to select just reads as a broken screen. */}
						<Typography variant="h6">
							{!filterOptions.hasProduction
								? "Nothing was rolled in the selected dates"
								: productSizeId == null
									? "Select a Product to load the report"
									: "No data available"}
						</Typography>
					</Box>
				) : (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<GroupsGrid
							groups={groups}
							stands={stands}
							onSelectionChange={setSelectedKeys}
							showLegendColor={displayMode === "straight"}
						/>

						<Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
							<Typography variant="body2" sx={{ fontWeight: 700 }}>
								Recipe:
							</Typography>
							<Typography variant="body2">{recipeLegend}</Typography>
						</Box>

						<SummaryGrid
							summary={summary}
							stands={stands}
							onGridReady={(api) => {
								summaryApiRef.current = api;
							}}
						/>

						<Divider sx={{ my: 0.5 }} />

						<InformationRetrievalChart
							groups={groups}
							selectedKeys={selectedKeys}
							stands={stands}
							displayMode={displayMode}
							onDisplayModeChange={setDisplayMode}
							rangeEnabled={rangeEnabled}
							onRangeEnabledChange={setRangeEnabled}
							recipeRangeEnabled={recipeRangeEnabled}
							onRecipeRangeEnabledChange={setRecipeRangeEnabled}
						/>
					</Box>
				)}
			</Paper>

			<BuildRecipeModal
				open={isRecipeModalOpen}
				productSizeId={productSizeId}
				start={startStr}
				end={endStr}
				onHide={() => setIsRecipeModalOpen(false)}
			/>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
